import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { ClipboardList, Check, X, Clock, Copy } from 'lucide-react'
import type { RegistrationRequest } from '@/types/admin'

type FilterStatus = 'pending' | 'approved' | 'rejected' | 'all'

function StatusBadge({ status }: { status: RegistrationRequest['status'] }) {
  const map: Record<
    RegistrationRequest['status'],
    { label: string; className: string; Icon: typeof Clock }
  > = {
    pending: { label: 'Menunggu', className: 'bg-yellow-100 text-yellow-700', Icon: Clock },
    approved: { label: 'Disetujui', className: 'bg-green-100 text-green-700', Icon: Check },
    rejected: { label: 'Ditolak', className: 'bg-red-100 text-red-700', Icon: X },
  }
  const { label, className, Icon } = map[status]
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  )
}

export default function RegistrationsPage() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<FilterStatus>('pending')
  const [rejectId, setRejectId] = useState<string | null>(null)
  const [rejectNotes, setRejectNotes] = useState('')
  const [approvedInfo, setApprovedInfo] = useState<{
    slug: string
    owner_email: string
    default_password: string
  } | null>(null)
  const [copied, setCopied] = useState(false)

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['admin', 'registrations'],
    queryFn: adminApi.getRegistrations,
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => adminApi.approveRegistration(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'registrations'] })
      setApprovedInfo({
        slug: data.slug,
        owner_email: data.owner_email,
        default_password: data.default_password,
      })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      adminApi.rejectRegistration(id, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'registrations'] })
      setRejectId(null)
      setRejectNotes('')
    },
  })

  const filtered =
    filter === 'all' ? requests : requests.filter((r) => r.status === filter)
  const pendingCount = requests.filter((r) => r.status === 'pending').length

  const filterLabels: Record<FilterStatus, string> = {
    pending: 'Menunggu',
    approved: 'Disetujui',
    rejected: 'Ditolak',
    all: 'Semua',
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Registrasi</h1>
        <p className="text-sm text-muted-foreground">
          Permintaan pendaftaran grup idol baru.
          {pendingCount > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
              {pendingCount} menunggu
            </span>
          )}
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex w-fit gap-1 rounded-lg border bg-muted/50 p-1">
        {(['pending', 'approved', 'rejected', 'all'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
              filter === s
                ? 'bg-background font-medium shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {filterLabels[s]}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <ClipboardList className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {filter === 'all'
                ? 'Belum ada permintaan pendaftaran.'
                : `Tidak ada permintaan dengan status "${filterLabels[filter]}".`}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Grup</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Subdomain</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">WhatsApp</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Daftar</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((req) => (
                <tr key={req.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{req.group_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {req.slug}.oneidol.id
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{req.owner_email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{req.whatsapp ?? '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={req.status} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDate(req.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    {req.status === 'pending' ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => approveMutation.mutate(req.id)}
                          disabled={approveMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check className="h-3 w-3" />
                          Approve
                        </button>
                        <button
                          onClick={() => setRejectId(req.id)}
                          disabled={rejectMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
                        >
                          <X className="h-3 w-3" />
                          Tolak
                        </button>
                      </div>
                    ) : (
                      req.notes && (
                        <p
                          className="max-w-[200px] truncate text-xs italic text-muted-foreground"
                          title={req.notes}
                        >
                          {req.notes}
                        </p>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Menampilkan {filtered.length} dari {requests.length} permintaan
      </p>

      {/* Reject modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-xl border bg-card p-5 shadow-xl">
            <h3 className="mb-1 font-semibold">Tolak Permintaan</h3>
            <p className="mb-3 text-sm text-muted-foreground">
              Anda dapat menyertakan alasan penolakan (opsional).
            </p>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Alasan penolakan..."
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="mt-3 flex gap-2">
              <button
                onClick={() =>
                  rejectMutation.mutate({ id: rejectId, notes: rejectNotes || undefined })
                }
                disabled={rejectMutation.isPending}
                className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Menolak...' : 'Konfirmasi Tolak'}
              </button>
              <button
                onClick={() => {
                  setRejectId(null)
                  setRejectNotes('')
                }}
                className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve success info modal */}
      {approvedInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl border bg-card p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              <h3 className="font-semibold">Tenant Berhasil Dibuat</h3>
            </div>
            <div className="space-y-2 rounded-lg bg-muted/50 p-3 text-sm">
              <div>
                <span className="text-muted-foreground">Site:</span>{' '}
                <span className="font-mono font-medium">{approvedInfo.slug}.oneidol.id</span>
              </div>
              <div>
                <span className="text-muted-foreground">Email owner:</span>{' '}
                <span className="font-medium">{approvedInfo.owner_email}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Plan:</span>{' '}
                <span className="font-medium">Free</span>
              </div>
            </div>

            {/* Default password section */}
            <div className="mt-3">
              <p className="mb-1.5 text-xs font-medium">Password Awal Owner</p>
              <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-2">
                <p className="flex-1 font-mono text-sm font-medium tracking-wider">
                  {approvedInfo.default_password}
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(approvedInfo.default_password)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                  className="shrink-0 rounded p-1 hover:bg-muted"
                  title="Salin password"
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Kirim email dan password ini ke owner via WhatsApp. Minta mereka ganti password setelah login pertama di Pengaturan → Akun.
              </p>
            </div>

            <button
              onClick={() => {
                setApprovedInfo(null)
                setCopied(false)
              }}
              className="mt-4 w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
